'use client';
import { cn } from '@base/utils/cn';
import { getWindow } from '@base/utils/dom';
import { Icon } from '@core/icons';
import { Button } from '@core/ui/components/atoms/button';
import { Container } from '@core/ui/components/atoms/container';
import { SimpleGridPattern } from '@core/ui/components/molecules/simple-grid-pattern';
import Link from 'next/link';
import { useEffect } from 'react';
import { boldTheme } from '#src/errors/themes/bold';
import { corporateTheme } from '#src/errors/themes/corporate';
import { minimalTheme } from '#src/errors/themes/minimal';
import { logNotFoundError } from '#src/errors/utils/error-logger';
import { jsxs, jsx } from 'react/jsx-runtime';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
var ENTRANCE_ANIMATION_CLASSES = "motion-safe:animate-in motion-safe:fade-in mot\
ion-safe:slide-in-from-bottom-6 motion-safe:duration-500";
function getThemeConfig(theme = "corporate") {
  if (theme === "minimal") {
    return minimalTheme;
  }
  if (theme === "bold") {
    return boldTheme;
  }
  return corporateTheme;
}
__name(getThemeConfig, "getThemeConfig");
function NotFoundTemplate({
  theme = "corporate",
  appName,
  title = "Page not found",
  message = "Sorry, we couldn't find the page you're looking for.",
  suggestions = [],
  showBackButton = true,
  enableLogging = true,
  logContext
}) {
  const themeConfig = getThemeConfig(theme);
  const win = getWindow();
  useEffect(() => {
    if (enableLogging) {
      logNotFoundError({
        path: win?.location?.pathname,
        appName,
        customContext: logContext
      });
    }
  }, [enableLogging, appName, logContext, win]);
  const handleBackClick = /* @__PURE__ */ __name(() => {
    if (win) {
      win.history.back();
    }
  }, "handleBackClick");
  return /* @__PURE__ */ jsxs("div", { className: "relative flex flex-auto items\
-center min-h-screen", children: [
    /* @__PURE__ */ jsx("div", { className: "absolute inset-0 -z-10 text-slate-9\
/10 dark:text-gray-11/10 [mask-image:linear-gradient(transparent,white,transpare\
nt)]", children: /* @__PURE__ */ jsx(SimpleGridPattern, { x: "50%", y: "50%", patternTransform: "\
translate(0 60)" }) }),
    /* @__PURE__ */ jsxs(
      Container,
      {
        className: cn(
          "flex flex-col items-center py-16 text-center sm:py-20 lg:py-32",
          ENTRANCE_ANIMATION_CLASSES
        ),
        "data-theme": theme,
        children: [
          /* @__PURE__ */ jsx("div", { className: "rounded-full bg-muted/50 p-4 \
mb-6", children: /* @__PURE__ */ jsx(
            Icon,
            {
              type: "lucide",
              icon: "FileQuestion",
              className: `${themeConfig.iconSize} text-muted-foreground`
            }
          ) }),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: `text-base font-medium tracking-tight text-foreground ${themeConfig.
              badgeStyle}`,
              children: "404"
            }
          ),
          /* @__PURE__ */ jsx(
            "h1",
            {
              className: `mt-6 font-display ${themeConfig.headingSize} tracking-\
tight text-foreground`,
              children: title
            }
          ),
          /* @__PURE__ */ jsx("p", { className: "mt-4 text-lg tracking-tight tex\
t-muted-foreground max-w-2xl", children: message }),
          suggestions.length > 0 && /* @__PURE__ */ jsx("div", { className: "mt-\
8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto", children: suggestions.map((suggestion) => /* @__PURE__ */ jsx(
          Button, { asChild: true, variant: "outline", children: /* @__PURE__ */ jsxs(
          Link, { href: suggestion.href, children: [
            suggestion.label,
            suggestion.description && /* @__PURE__ */ jsxs("span", { className: "\
sr-only", children: [
              " - ",
              suggestion.description
            ] })
          ] }) }, suggestion.href)) }),
          suggestions.length === 0 && /* @__PURE__ */ jsxs(
            Link,
            {
              href: "/",
              className: "mt-6 text-base font-medium text-primary hover:text-pri\
mary/80 transition-colors inline-flex items-center gap-2",
              children: [
                /* @__PURE__ */ jsx("span", { children: "Go back home" }),
                /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\
→" })
              ]
            }
          ),
          showBackButton && /* @__PURE__ */ jsxs(Button, { variant: "ghost", onClick: handleBackClick,
          className: "mt-4", children: [
            /* @__PURE__ */ jsx(Icon, { type: "lucide", icon: "ArrowLeft", className: "\
mr-2 h-4 w-4" }),
            "Go back"
          ] })
        ]
      }
    )
  ] });
}
__name(NotFoundTemplate, "NotFoundTemplate");

export { NotFoundTemplate };
